// src/components/CallNumber.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./CallNumber.css";

/**
 * CallNumber（單一叫號機版）
 * - 由 parent 傳入 `id`（如 3500 或 "3500"）
 * - 置中顯示：號碼 / 休診中 / 連線中…
 * - 七段數位風格
 * - 每 25 秒重新查詢
 * - 斷線自動退避重連
 * - React 18 StrictMode 友善
 */

// MainPi 新 WebSocket API
const WS_URL =
  "wss://www.mainpi.com/.ws_mainpi/?token=chiichungclinic:41155b8118ba120bc57166f39db2420c";

export default function CallNumber({ id = "3500", label = "", size = "sm" }) {
  const targetId = String(id);

  // 超過此時間沒有更新，就顯示休診
  const STALE_MINUTES = 10;

  // null = 連線中
  // "-"  = 休診
  // 其它 = 目前號碼
  const [value, setValue] = useState(null);

  // MainPi 回傳距離最後更新的分鐘數
  const [lastUpdate, setLastUpdate] = useState(null);

  // idle | connecting | open | closed | error
  const [conn, setConn] = useState("idle");

  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const stoppedRef = useRef(false);
  const backoffRef = useRef(1000);

  // =========================================================
  // call_nums 解析
  // 支援：
  // number
  // string
  // array
  // { A,B,C,D }
  // { 0,1,2,3 }
  // =========================================================
  const pickNumber = useCallback((callNums) => {
    const isOff = (v) => {
      if (v == null) return true;

      const s = String(v).trim();

      return s === "" || s === "-" || s === "—" || s === "0";
    };

    try {
      // number
      if (typeof callNums === "number") {
        return isOff(callNums) ? "-" : String(callNums);
      }

      // string
      if (typeof callNums === "string") {
        const s = callNums.trim();

        if (!s) return "-";

        // 如果本身其實是一段 JSON
        if (s.startsWith("{") || s.startsWith("[") || s.startsWith('"')) {
          try {
            const parsed = JSON.parse(s);
            return pickNumber(parsed);
          } catch {
            // 不是 JSON 就直接當普通字串
          }
        }

        return isOff(s) ? "-" : s;
      }

      // array
      if (Array.isArray(callNums)) {
        for (const v of callNums) {
          if (!isOff(v)) {
            return String(v).trim();
          }
        }

        return "-";
      }

      // object
      if (callNums && typeof callNums === "object") {
        const byLetter = [
          callNums.A ?? callNums.a,
          callNums.B ?? callNums.b,
          callNums.C ?? callNums.c,
          callNums.D ?? callNums.d,
        ];

        const byIndex = [
          callNums["0"],
          callNums["1"],
          callNums["2"],
          callNums["3"],
        ];

        const pool = byLetter.some((v) => v !== undefined) ? byLetter : byIndex;

        for (const v of pool) {
          if (!isOff(v)) {
            return String(v).trim();
          }
        }

        return "-";
      }

      return "-";
    } catch (err) {
      console.warn("[CallNumber] pickNumber error:", err);
      return "-";
    }
  }, []);

  // =========================================================
  // 收到 MainPi WebSocket 資料
  // =========================================================
  const onMessage = useCallback(
    (e) => {
      try {
        console.log("[MainPI] raw message:", e.data);

        const parsed = JSON.parse(e.data || "[]");

        console.log("[MainPI] parsed message:", parsed);

        // 預期主要格式為 array
        let callers = parsed;

        // 若未來 MainPi 改成：
        // { data: [...] }
        // 也先相容
        if (!Array.isArray(callers) && Array.isArray(parsed?.data)) {
          callers = parsed.data;
        }

        if (!Array.isArray(callers)) {
          console.warn("[MainPI] unexpected response:", parsed);
          return;
        }

        let matched = false;

        for (const c of callers) {
          if (!c || c.id == null) continue;

          if (String(c.id) !== targetId) {
            continue;
          }

          matched = true;

          console.log("[MainPI] matched device:", c);

          const nextValue = pickNumber(c.call_nums);

          setValue(nextValue);

          if (typeof c.last_update === "number") {
            setLastUpdate(c.last_update);
          } else {
            setLastUpdate(null);
          }

          break;
        }

        if (!matched) {
          console.warn(`[MainPI] device ${targetId} not found in response`);
        }
      } catch (err) {
        console.warn("[CallNumber] parse error:", err);
        console.log("[MainPI] unparsed message:", e.data);
      }
    },
    [pickNumber, targetId],
  );

  // =========================================================
  // 安全送出 WebSocket 訊息
  // =========================================================
  const safeSend = useCallback((payload) => {
    const ws = wsRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.warn("[MainPI] send failed:", err);
      return false;
    }
  }, []);

  // =========================================================
  // 查詢目前診間
  // =========================================================
  const query = useCallback(() => {
    console.log("[MainPI] query device:", targetId);

    return safeSend({
      type: "query",
      text: targetId,
    });
  }, [safeSend, targetId]);

  // =========================================================
  // 重連
  // =========================================================
  const reconnect = useCallback(() => {
    if (stoppedRef.current) return;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }

    const delay = backoffRef.current;

    backoffRef.current = Math.min(delay * 2, 20000);

    console.log(`[MainPI] reconnect after ${delay} ms`);

    reconnectTimerRef.current = setTimeout(() => {
      if (!stoppedRef.current) {
        connectRef.current?.();
      }
    }, delay);
  }, []);

  // 用 ref 避免 connect / reconnect callback 相互依賴
  const connectRef = useRef(null);

  // =========================================================
  // 建立 WebSocket
  // =========================================================
  const connect = useCallback(() => {
    if (stoppedRef.current) return;

    const current = wsRef.current;

    if (
      current &&
      (current.readyState === WebSocket.OPEN ||
        current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    setConn("connecting");

    try {
      console.log("[MainPI] connecting:", WS_URL);

      const ws = new WebSocket(WS_URL);

      wsRef.current = ws;

      // -------------------------------------------------------
      // Connection opened
      // -------------------------------------------------------
      ws.onopen = () => {
        if (stoppedRef.current) {
          try {
            ws.close();
          } catch {}

          return;
        }

        console.log("[MainPI] WebSocket connected");

        setConn("open");

        backoffRef.current = 1000;

        // 第一次立即查詢
        query();

        // 清除舊 timer
        if (pollRef.current) {
          clearInterval(pollRef.current);
        }

        // 每 25 秒重新查詢
        pollRef.current = setInterval(() => {
          if (stoppedRef.current) return;

          console.log("[MainPI] refresh device:", targetId);

          const ok = query();

          if (!ok) {
            reconnect();
          }
        }, 25000);
      };

      // -------------------------------------------------------
      // Message received
      // -------------------------------------------------------
      ws.onmessage = onMessage;

      // -------------------------------------------------------
      // Connection closed
      // -------------------------------------------------------
      ws.onclose = (event) => {
        console.log("[MainPI] WebSocket closed:", event.code, event.reason);

        setConn("closed");

        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }

        if (!stoppedRef.current) {
          reconnect();
        }
      };

      // -------------------------------------------------------
      // Connection error
      // -------------------------------------------------------
      ws.onerror = (event) => {
        console.error("[MainPI] WebSocket error:", event);

        setConn("error");

        // 通常 browser 之後仍會觸發 onclose
        // 因此這裡不主動 reconnect，避免重複 timer
      };
    } catch (err) {
      console.error("[MainPI] connection exception:", err);

      setConn("error");

      reconnect();
    }
  }, [onMessage, query, reconnect, targetId]);

  // 將最新 connect 保存給 reconnect 使用
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // =========================================================
  // 初始化 / Cleanup
  // =========================================================
  useEffect(() => {
    stoppedRef.current = false;

    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        connect();
      }
    };

    const handleOnline = () => {
      connect();
    };

    connect();

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("online", handleOnline);

    return () => {
      stoppedRef.current = true;

      document.removeEventListener("visibilitychange", handleVisible);

      window.removeEventListener("online", handleOnline);

      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const ws = wsRef.current;

      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onclose = null;
          ws.onerror = null;

          if (
            ws.readyState === WebSocket.OPEN ||
            ws.readyState === WebSocket.CLOSING
          ) {
            ws.close(1000, "component unmount");
          } else if (ws.readyState === WebSocket.CONNECTING) {
            const closeAfterOpen = () => {
              try {
                ws.close(1000, "component unmount (late)");
              } catch {}
            };

            ws.addEventListener("open", closeAfterOpen, {
              once: true,
            });
          }
        } catch (err) {
          console.warn("[MainPI] cleanup WebSocket error:", err);
        }

        wsRef.current = null;
      }
    };
  }, [connect]);

  // =========================================================
  // 七段顯示
  // =========================================================
  const SEG_MAP = {
    0: ["a", "b", "c", "d", "e", "f"],
    1: ["b", "c"],
    2: ["a", "b", "g", "e", "d"],
    3: ["a", "b", "g", "c", "d"],
    4: ["f", "g", "b", "c"],
    5: ["a", "f", "g", "c", "d"],
    6: ["a", "f", "g", "e", "c", "d"],
    7: ["a", "b", "c"],
    8: ["a", "b", "c", "d", "e", "f", "g"],
    9: ["a", "b", "c", "d", "f", "g"],

    A: ["a", "b", "c", "e", "f", "g"],
    B: ["f", "g", "e", "c", "d"],
    C: ["a", "f", "e", "d"],
    D: ["b", "c", "d", "e", "g"],
    E: ["a", "f", "g", "e", "d"],
    F: ["a", "f", "g", "e"],

    "-": ["g"],
    " ": [],
  };

  const SegDigit = ({ ch }) => {
    const raw = (ch || "").toString();

    const key = raw.length === 1 ? raw.toUpperCase() : raw;

    const segs = SEG_MAP[key] || [];

    const on = (segment) => segs.includes(segment);

    return (
      <span className="seg7-digit" aria-hidden="true">
        <i className={`seg7-seg a ${on("a") ? "on" : ""}`} />
        <i className={`seg7-seg b ${on("b") ? "on" : ""}`} />
        <i className={`seg7-seg c ${on("c") ? "on" : ""}`} />
        <i className={`seg7-seg d ${on("d") ? "on" : ""}`} />
        <i className={`seg7-seg e ${on("e") ? "on" : ""}`} />
        <i className={`seg7-seg f ${on("f") ? "on" : ""}`} />
        <i className={`seg7-seg g ${on("g") ? "on" : ""}`} />
      </span>
    );
  };

  // =========================================================
  // Render number
  // =========================================================
  const renderDigits = (val, small = false) => {
    const isStale =
      typeof lastUpdate === "number" && lastUpdate >= STALE_MINUTES;

    if (val == null) {
      return <span style={{ color: "#94a3b8" }}>連線中…</span>;
    }

    if (val === "-" || isStale) {
      return <span className="cn-badge">休診中</span>;
    }

    const chars = String(val).split("");

    return (
      <div
        className={`cn-digits seg7 ` + `${small || size === "sm" ? "sm" : ""}`}
        aria-label={`目前號碼：${val}`}
      >
        {chars.map((ch, index) => (
          <SegDigit key={`${ch}-${index}`} ch={ch} />
        ))}
      </div>
    );
  };

  // =========================================================
  // UI
  // =========================================================
  return (
    <section className="cn-card" aria-label="線上叫號">
      <div
        className="cn-header"
        style={{
          justifyContent: "space-between",
        }}
      >
        <div className="cn-titleWrap">
          {label ? <div className="cn-title">{label}</div> : null}

          <div className="cn-display compact fit4">
            {renderDigits(value, true)}

            {/* WebSocket 狀態 */}
            <div className="cn-connStatus">
              <span
                className={
                  "cn-dot " +
                  (conn === "open"
                    ? "ok"
                    : conn === "connecting"
                      ? "connecting"
                      : conn === "error"
                        ? "error"
                        : "offline")
                }
                aria-hidden="true"
              />

              <span>
                {conn === "open"
                  ? "連線正常"
                  : conn === "connecting"
                    ? "連線中…"
                    : conn === "error"
                      ? "連線異常，重試中…"
                      : "離線"}
              </span>
            </div>

            {/* MainPi 最後更新狀態 */}
            <div className="cn-status">
              {lastUpdate === 0
                ? "即時更新"
                : typeof lastUpdate === "number"
                  ? `約 ${lastUpdate} 分鐘前更新`
                  : ""}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
